import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Sparkles, DollarSign, Users, 
  Settings, LogOut, ChevronLeft, ChevronRight, Building2,
  BarChart3, Gift, Bell, HelpCircle, QrCode, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { usePartnerNotifications } from '@/hooks/usePartnerNotifications';
import { cn } from '@/lib/utils';

const navItems = [
  { 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    href: '/partner/dashboard',
    description: 'Overview & metrics'
  },
  { 
    label: 'Revenue', 
    icon: DollarSign, 
    href: '/partner/revenue',
    description: 'Earnings & payouts',
    badge: 'New'
  },
  { 
    label: 'Analytics', 
    icon: BarChart3, 
    href: '/partner/analytics',
    description: 'Performance metrics'
  },
  { 
    label: 'Events', 
    icon: Calendar, 
    href: '/partner/events',
    description: 'Manage events'
  },
  { 
    label: 'Experiences', 
    icon: Gift, 
    href: '/partner/experiences',
    description: 'Vouchers & offers'
  },
  { 
    label: 'AI Marketing', 
    icon: Sparkles, 
    href: '/partner/ai-marketing',
    description: 'Generate content'
  },
  { 
    label: 'Reservations', 
    icon: Users, 
    href: '/partner/reservations',
    description: 'Bookings & inquiries'
  },
  { 
    label: 'Redemptions', 
    icon: QrCode, 
    href: '/partner/redemptions',
    description: 'Verify vouchers'
  },
  { 
    label: 'Notifications', 
    icon: Bell, 
    href: '/partner/notifications',
    description: 'Alert preferences'
  },
];

const bottomNavItems = [
  { label: 'Help & Support', icon: HelpCircle, href: '#' },
  { label: 'Private Hire', icon: Building2, href: '/partner/private-hire' },
  { label: 'Settings', icon: Settings, href: '/partner/settings' },
];

export const PartnerSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-gray-900 text-white transition-all duration-300 flex flex-col",
        collapsed ? "w-20" : "w-64"
      )}
      data-testid="partner-sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
        {!collapsed && (
          <Link to="/partner/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <span className="font-bold text-lg headline-serif">Partner Hub</span>
          </Link>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center mx-auto">
            <Building2 className="w-5 h-5" />
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="text-gray-400 hover:text-white hover:bg-gray-800 hidden lg:flex"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div className="px-4 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center font-bold">
              {user?.full_name?.charAt(0) || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name || 'Partner'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group relative",
                isActive 
                  ? "bg-indigo-600 text-white" 
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <>
                  <div className="flex-1">
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.description && (
                      <p className="text-xs text-gray-500 group-hover:text-gray-400">{item.description}</p>
                    )}
                  </div>
                  {item.badge && (
                    <Badge className="bg-green-500 text-white text-xs px-1.5 py-0">
                      {item.badge}
                    </Badge>
                  )}
                </>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 py-4 border-t border-gray-800 space-y-1">
        {/* Admin Dashboard link - only for admins */}
        {user?.role === 'admin' && (
          <Link
            to="/admin"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-amber-400 hover:text-amber-300 hover:bg-gray-800 transition-colors"
            data-testid="nav-admin-dashboard"
          >
            <ShieldCheck className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Admin Dashboard</span>}
          </Link>
        )}
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          
          if (item.href === '#') {
            return (
              <button
                key={item.label}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          }
          
          return (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                isActive 
                  ? "bg-indigo-600 text-white" 
                  : "text-gray-400 hover:text-white hover:bg-gray-800"
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-gray-800 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
};

export const PartnerHeader = ({ title, subtitle, actions, unreadCount = 0 }) => {
  const navigate = useNavigate();
  return (
    <header className="h-16 bg-white dark:bg-gray-900 border-b flex items-center justify-between px-6">
      <div>
        <h1 className="text-xl font-bold text-foreground headline-serif">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">
        {actions}
        <Button variant="ghost" size="icon" className="relative" data-testid="partner-notification-bell" onClick={() => navigate('/partner/notifications')}>
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    </header>
  );
};

export const PartnerLayout = ({ children, title, subtitle, actions }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { unreadCount } = usePartnerNotifications();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <PartnerSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        <PartnerHeader title={title} subtitle={subtitle} actions={actions} unreadCount={unreadCount} />
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default PartnerLayout;
